import { create } from 'zustand';

export type ToastSeverity = 'success' | 'info' | 'warning' | 'error';

export interface ToastItem {
  id: string;
  message: string;
  severity: ToastSeverity;
  durationMs: number;
}

interface ToastState {
  toasts: ToastItem[];
  show: (message: string, options?: { severity?: ToastSeverity; durationMs?: number }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const DEFAULT_DURATION_MS = 5000;

let counter = 0;
function nextId(): string {
  counter += 1;
  return `t-${Date.now().toString(36)}-${counter}`;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  show: (message, options) => {
    const id = nextId();
    const toast: ToastItem = {
      id,
      message,
      severity: options?.severity ?? 'info',
      durationMs: options?.durationMs ?? DEFAULT_DURATION_MS,
    };
    set((state) => ({ toasts: [...state.toasts, toast] }));
    return id;
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

export function useToast(): {
  success: (message: string, durationMs?: number) => string;
  info: (message: string, durationMs?: number) => string;
  warning: (message: string, durationMs?: number) => string;
  error: (message: string, durationMs?: number) => string;
  dismiss: (id: string) => void;
} {
  const show = useToastStore((s) => s.show);
  const dismiss = useToastStore((s) => s.dismiss);
  return {
    success: (m, d) => show(m, { severity: 'success', durationMs: d }),
    info: (m, d) => show(m, { severity: 'info', durationMs: d }),
    warning: (m, d) => show(m, { severity: 'warning', durationMs: d }),
    error: (m, d) => show(m, { severity: 'error', durationMs: d ?? 8000 }),
    dismiss,
  };
}
